import { posthog } from "@/lib/posthog"
import { toast } from "sonner"
import {
  DeleteV2ItemRequest,
  GetPendingSheetsResponse,
  GetV2LastHistoryResponse,
  StartV2ShiftRequest,
  SubmitV2SheetsRequest,
  V2AllSchemasResponse,
  V2SingleDeptSchemaResponse,
} from "@/packaging-v2/lib/schema"
import { api } from "@/services/base"
import useStore from "@/store"
import { V2DeptHistory } from "@/store/packaging-v2"
import { HTTPError } from "ky"
import { useRef } from "react"
import { useInterval } from "react-use"

const BASE_URL = "packaging-v2"

// ============================================
// Schema APIs
// ============================================

/**
 * Get all department schemas for the employee's plant
 * Returns normalized structure with separate departments and products
 */
export async function getAllV2DeptSchemas(): Promise<V2AllSchemasResponse> {
  const res = await api.get<V2AllSchemasResponse>(
    `${BASE_URL}/department-schemas`
  )
  return res.json()
}

/**
 * Get schema for a specific department
 * Returns normalized structure with the department and its products
 */
export async function getV2DeptSchema(
  departmentId: string
): Promise<V2SingleDeptSchemaResponse> {
  const res = await api.get<V2SingleDeptSchemaResponse>(
    `${BASE_URL}/department-schema/${departmentId}`
  )
  return res.json()
}

// ============================================
// Shift Management APIs
// ============================================

/**
 * Start a new shift session for Packaging V2
 */
export async function startV2Shift(
  payload: StartV2ShiftRequest
): Promise<void> {
  await api.post(`${BASE_URL}/shift/start`, {
    json: payload,
  })

  const store = useStore.getState()
  store.packagingV2.startShift(payload)
}

/**
 * End the current shift, completing all submitted sheets
 */
export async function endV2Shift(): Promise<void> {
  const store = useStore.getState()

  try {
    await api.get(`${BASE_URL}/shift/end`)
    store.session.endProcess(store.packagingV2.shift.processId)
    store.packagingV2.clearShift()
  } catch (err) {
    if (err instanceof HTTPError) {
      if (err.response.status === 500) {
        store.packagingV2.clearShift()
      }
    }
    throw err
  }
}

/**
 * Discard the current shift, marking all sheets as deleted
 */
export async function discardV2Shift(): Promise<void> {
  const store = useStore.getState()

  try {
    await api.get(`${BASE_URL}/shift/discard`)
    store.packagingV2.clearShift()
  } catch (err) {
    if (err instanceof HTTPError) {
      if (err.response.status === 500) {
        store.packagingV2.clearShift()
      }
    }
    throw err
  }
}

// ============================================
// Sheet Submission APIs
// ============================================

/**
 * Submit one or more sheets with items and parameter readings
 */
export async function submitV2Sheets(
  sheets: SubmitV2SheetsRequest
): Promise<void> {
  await api.post(`${BASE_URL}/sheets/submit`, {
    json: sheets,
  })
}

/**
 * Delete one item from a sheet for a specific department/machine
 */
export async function deleteV2Item(
  params: DeleteV2ItemRequest
): Promise<void> {
  await api.post(`${BASE_URL}/sheet/delete`, {
    json: params,
  })
}

// ============================================
// History APIs
// ============================================

/**
 * Get last completed sheet for each machine in a department
 */
export async function getV2LastHistory(
  departmentId: string
): Promise<V2DeptHistory> {
  const res = await api
    .get<GetV2LastHistoryResponse>(`${BASE_URL}/${departmentId}/last-history`)
    .json()

  const history: V2DeptHistory = {
    id: departmentId,
    sheets: res.sheets,
  }

  const store = useStore.getState()
  store.packagingV2.addHistory(history)

  return history
}

/**
 * Get sheets for a process session
 * @param employeeSessionId - The ID of the employee session (from employeeSessions array)
 */
export async function getSessionSheets(employeeSessionId: string): Promise<GetPendingSheetsResponse> {
  const res = await api.get<GetPendingSheetsResponse>(
    `${BASE_URL}/session-sheets?employeeSessionId=${employeeSessionId}`
  )
  return res.json()
}

// ============================================
// Background Submission Hook
// ============================================

export interface V2FormSubmission {
  deptName: string
  machineName: string
  deptId: string
  machineId: string
  itemIndex: number
}

/**
 * Hook to process V2 form submissions in the background with retry logic
 */
export function useSubmitV2Forms() {
  const isSubmitting = useRef(false)
  const retryCount = useRef(0)
  const packagingV2Store = useStore.use.packagingV2()
  const MAX_RETRIES = 3

  useInterval(
    async () => {
      try {
        if (isSubmitting.current) {
          return
        }

        isSubmitting.current = true

        const oldest = packagingV2Store.getNextSubmission()
        if (!oldest) {
          isSubmitting.current = false
          return
        }

        // Get form data for this submission
        const machineFormData = packagingV2Store.getFormData(
          oldest.deptId,
          oldest.machineId
        )
        if (!machineFormData) {
          packagingV2Store.dequeOldestSubmission()
          isSubmitting.current = false
          return
        }

        // Build the submission payload
        const payload = packagingV2Store.buildSubmissionPayload(
          oldest.deptId,
          oldest.machineId
        )

        if (!payload || payload.length === 0) {
          packagingV2Store.dequeOldestSubmission()
          isSubmitting.current = false
          return
        }

        await submitV2Sheets(payload)

        // On success: mark item as submitted in local store, then dequeue
        packagingV2Store.markItemAsSubmitted(
          oldest.deptId,
          oldest.machineId,
          oldest.itemIndex
        )
        packagingV2Store.dequeOldestSubmission()
        toast.success("Readings submitted successfully")
        isSubmitting.current = false
        retryCount.current = 0
      } catch (error) {
        retryCount.current += 1
        isSubmitting.current = false
        console.error("V2 submission error:", error)

        if (retryCount.current >= MAX_RETRIES) {
          // After max retries: show error toast, capture exception, and dequeue
          toast.error(
            `Failed to submit readings after ${MAX_RETRIES} attempts. Please try again.`
          )
          posthog.captureException(error, {
            location: "packaging-v2-sync",
          })
          packagingV2Store.dequeOldestSubmission()
          retryCount.current = 0
        }
      }
    },
    1000
  )
}
